'use client';

/**
 * NotificationInbox Component
 *
 * Full-page notification inbox with tabs (All/Unread/Read), search,
 * grouped notifications, and mark-all-read. Apple Liquid Glass styling.
 * Designed for the /notifications page across all portals.
 *
 * @see Issue #699 - Notification System Fidelity
 * @see Apple WWDC 2025 - Liquid Glass design language
 */

import MarkAllReadIcon from '@mui/icons-material/DoneAll';
import InboxEmptyIcon from '@mui/icons-material/Inbox';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { springPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton';
import { GlassTabs } from '../GlassTabs';
import { GlassTextField } from '../GlassTextField';
import type { NotificationItemData } from '../NotificationItem';
import { NotificationItem } from '../NotificationItem';
import { ShimmerLoader } from '../ShimmerLoader';

// ============================================================================
// Types
// ============================================================================

export type NotificationInboxTab = 'all' | 'unread' | 'read';

export interface NotificationInboxProps {
  /** Notification items */
  notifications: NotificationItemData[];

  /** Whether notifications are loading */
  loading?: boolean;

  /** Whether there are more notifications to load */
  hasMore?: boolean;

  /** Called when "Load more" is clicked */
  onLoadMore?: () => void;

  /** Called when "Mark all as read" is clicked */
  onMarkAllRead?: () => void;

  /** Called when a notification is clicked */
  onNotificationClick?: (notification: NotificationItemData) => void;

  /** Called when toggle read is triggered */
  onToggleRead?: (notification: NotificationItemData) => void;

  /** Called when archive is triggered */
  onArchive?: (notification: NotificationItemData) => void;

  /** Called when delete is triggered */
  onDelete?: (notification: NotificationItemData) => void;

  /** Called when search query changes */
  onSearch?: (query: string) => void;

  /** Called when tab changes */
  onTabChange?: (tab: NotificationInboxTab) => void;

  /** Active tab */
  activeTab?: NotificationInboxTab;
}

// ============================================================================
// Component
// ============================================================================

export const NotificationInbox = forwardRef<HTMLDivElement, NotificationInboxProps>(
  (
    {
      notifications,
      loading = false,
      hasMore = false,
      onLoadMore,
      onMarkAllRead,
      onNotificationClick,
      onToggleRead,
      onArchive,
      onDelete,
      onSearch,
      onTabChange,
      activeTab = 'all',
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { t } = useI18n();
    const [searchQuery, setSearchQuery] = useState('');

    const filteredNotifications = useMemo(() => {
      let items = notifications;

      // Filter by tab
      if (activeTab === 'unread') {
        items = items.filter((n) => !n.isRead);
      } else if (activeTab === 'read') {
        items = items.filter((n) => n.isRead);
      }

      // Filter by search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        items = items.filter((n) => n.title.toLowerCase().includes(q) || n.message.toLowerCase().includes(q));
      }

      return items;
    }, [notifications, activeTab, searchQuery]);

    const unreadCount = notifications.filter((n) => !n.isRead).length;
    const hasUnread = unreadCount > 0;

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchQuery(value);
        onSearch?.(value);
      },
      [onSearch],
    );

    const handleTabChange = useCallback(
      (_: unknown, value: string) => {
        onTabChange?.(value as NotificationInboxTab);
      },
      [onTabChange],
    );

    const tabs = useMemo(
      () => [
        {
          value: 'all',
          label: t('notification.inbox.all'),
        },
        {
          value: 'unread',
          label: `${t('notification.inbox.unread')}${unreadCount > 0 ? ` (${unreadCount})` : ''}`,
        },
        {
          value: 'read',
          label: t('notification.inbox.read'),
        },
      ],
      [t, unreadCount],
    );

    const emptyMessage = useMemo(() => {
      if (activeTab === 'unread') {
        return {
          title: t('notification.inbox.emptyUnread'),
          description: t('notification.inbox.emptyUnreadDescription'),
        };
      }
      return {
        title: t('notification.inbox.empty'),
        description: t('notification.inbox.emptyDescription'),
      };
    }, [activeTab, t]);

    return (
      <Box ref={ref} sx={{ width: '100%', maxWidth: 680, mx: 'auto' }}>
        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          {/* Search */}
          <GlassTextField
            size="small"
            placeholder={t('notification.inbox.search')}
            value={searchQuery}
            onChange={handleSearchChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: iconSizes.md, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ flex: 1, maxWidth: 320 }}
          />

          {/* Mark all read */}
          {hasUnread && onMarkAllRead && (
            <GlassButton
              variant="outlined"
              size="small"
              onClick={onMarkAllRead}
              startIcon={<MarkAllReadIcon sx={{ fontSize: iconSizes.sm }} />}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              {t('notification.inbox.markAllRead')}
            </GlassButton>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ mb: 2 }}>
          <GlassTabs value={activeTab} onChange={handleTabChange} tabs={tabs} />
        </Box>

        {/* Notification list */}
        <Box
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            // Liquid Glass card surface
            background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
            backdropFilter: 'blur(20px) saturate(150%)',
            WebkitBackdropFilter: 'blur(20px) saturate(150%)',
            border: `0.5px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.6)'}`,
            boxShadow: isDark
              ? '0 2px 16px rgba(0,0,0,0.3), inset 0 0.5px 0 rgba(255,255,255,0.03)'
              : '0 2px 16px rgba(0,0,0,0.05), inset 0 0.5px 0 rgba(255,255,255,0.5)',
          }}
        >
          {loading && filteredNotifications.length === 0 ? (
            <Box sx={{ p: 2.5 }}>
              {[0, 1, 2, 3].map((i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <ShimmerLoader sx={{ height: 64, borderRadius: 2 }} />
                </Box>
              ))}
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box sx={{ py: 8, px: 4, textAlign: 'center' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={springPresets.gentle}
              >
                <InboxEmptyIcon
                  sx={{
                    fontSize: iconSizes['3xl'],
                    color: 'text.disabled',
                    mb: 2,
                    opacity: 0.5,
                  }}
                />
                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5 }}>
                  {emptyMessage.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  {emptyMessage.description}
                </Typography>
              </motion.div>
            </Box>
          ) : (
            <>
              <AnimatePresence mode="popLayout">
                {filteredNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClick={onNotificationClick}
                    onToggleRead={onToggleRead}
                    onArchive={onArchive}
                    onDelete={onDelete}
                  />
                ))}
              </AnimatePresence>

              {/* Load more */}
              {hasMore && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <GlassButton
                    variant="text"
                    size="small"
                    onClick={onLoadMore}
                    loading={loading}
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    {t('notification.inbox.loadMore')}
                  </GlassButton>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  },
);

NotificationInbox.displayName = 'NotificationInbox';

export default NotificationInbox;
